import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { SiteSection, SiteSectionHeader } from '../../../../components/site';
import { securityCompliance, securityStats } from '../data';
import type { PlatformSubpageConfig } from '../types';

function formatStat(value: number, decimals: number, suffix: string): string {
  if (suffix === '%') return `${value.toFixed(decimals)}%`;
  if (suffix === 'M+') return `${value}${suffix}`;
  if (suffix === '-bit') return `${value}${suffix}`;
  return `${value}${suffix}`;
}

export const SecurityContent: React.FC<{ config: PlatformSubpageConfig }> = ({ config }) => {
  const [openId, setOpenId] = useState<string>(securityCompliance[0].id);
  const [animated, setAnimated] = useState(false);
  const [values, setValues] = useState<number[]>(securityStats.map(() => 0));
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated) {
          setAnimated(true);
          securityStats.forEach((stat, i) => {
            const target = stat.value;
            const start = performance.now();
            const duration = 1200;
            const tick = (now: number) => {
              const t = Math.min(1, (now - start) / duration);
              setValues((prev) => {
                const next = [...prev];
                next[i] = target * t;
                return next;
              });
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          });
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [animated]);

  return (
    <SiteSection className="platform-sub-interactive">
      <SiteSectionHeader
        eyebrow="INTERACTIVE"
        title={config.sections.title}
        subtitle={config.sections.subtitle}
        align="center"
      />
      <div ref={statsRef} className="platform-sub-stats platform-sub-stats--premium">
        {securityStats.map((stat, i) => (
          <div key={stat.label} className="platform-sub-stat">
            <span className="platform-sub-stat__value">
              {formatStat(values[i], stat.decimals, stat.suffix)}
            </span>
            <span className="platform-sub-stat__label">{stat.label}</span>
          </div>
        ))}
      </div>
      <div className="platform-sub-accordion platform-sub-accordion--light">
        {securityCompliance.map((item) => {
          const isOpen = openId === item.id;
          return (
            <div key={item.id} className="platform-sub-accordion__item">
              <button
                type="button"
                className="platform-sub-accordion__trigger"
                aria-expanded={isOpen}
                onClick={() => setOpenId(isOpen ? '' : item.id)}
              >
                <span>{item.title}</span>
                <ChevronDown
                  size={18}
                  className={`platform-sub-accordion__icon${isOpen ? ' platform-sub-accordion__icon--open' : ''}`}
                  aria-hidden
                />
              </button>
              {isOpen ? (
                <div className="platform-sub-accordion__body">
                  <p>{item.body}</p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </SiteSection>
  );
};
